/**
 * Portal Context (MIRA 2.0)
 *
 * Central state for portal authentication. At start-up a `?token=` from the
 * PARK app is exchanged for a session token (sent as X-Portal-Session); validate-access then
 * says who the session belongs to:
 *  - a medarbejder: the portal runs as them.
 *  - an internal Mira user without a medarbejder (admin mode): they pick a salon in their
 *    Mira scope and a medarbejder, and the session impersonates that person server-side
 *    (POST /portal/admin/impersonation). `actor` is the internal user behind the session.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type {
  Actor,
  Employee,
  PortalValidationData,
  Salon,
  SalonListItem,
  PortalState,
} from '../types/portal.types';
import {
  exchangePortalToken,
  validatePortalAccess,
  getAdminSalons,
  getAdminEmployees,
  startImpersonation,
  stopImpersonation as endImpersonation,
} from '../services/portalService';
import { readPortalToken, stripPortalTokenFromUrl } from '../utils/urlParams';
import { isAuthError, setAuthErrorHandler } from '../services/api';

export const SESSION_EXPIRED_MESSAGE = 'Din adgang er udløbet. Åbn portalen igen fra appen.';

const initialState: PortalState = {
  isLoading: false,
  isAuthenticated: false,
  error: null,
  employee: null,
  salon: null,
  isOwner: false,
  isAdminMode: false,
  actor: null,
  availableSalons: [],
  availableEmployees: [],
  selectedSalonId: null,
};

type PortalAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUTH_SUCCESS'; payload: { employee: Employee; salon: Salon; isOwner: boolean; actor: Actor | null } }
  | { type: 'SET_ADMIN_MODE'; payload: { salons: SalonListItem[]; actor: Actor | null } }
  | { type: 'SET_AVAILABLE_EMPLOYEES'; payload: { employees: Employee[] } }
  | { type: 'SELECT_SALON'; payload: number }
  | { type: 'RESET' };

function portalReducer(state: PortalState, action: PortalAction): PortalState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, isLoading: false, error: action.payload };

    case 'SET_AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        employee: action.payload.employee,
        salon: action.payload.salon,
        isOwner: action.payload.isOwner,
        actor: action.payload.actor,
        isAdminMode: false,
      };

    case 'SET_ADMIN_MODE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        error: null,
        isAdminMode: true,
        employee: null,
        salon: null,
        isOwner: false,
        actor: action.payload.actor,
        availableSalons: action.payload.salons,
        availableEmployees: [],
        selectedSalonId: null,
      };

    case 'SET_AVAILABLE_EMPLOYEES':
      return { ...state, isLoading: false, availableEmployees: action.payload.employees };

    case 'SELECT_SALON':
      return { ...state, selectedSalonId: action.payload, availableEmployees: [] };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

interface PortalContextValue extends PortalState {
  initializePortal: () => Promise<void>;
  selectSalon: (salonId: number) => Promise<void>;
  /** Starter en impersonation af medarbejderen (kun i admin-tilstand). */
  selectEmployee: (employeeId: number) => Promise<void>;
  /** Afslutter impersonationen og vender tilbage til admin-tilstand. */
  stopImpersonation: () => Promise<void>;
  reset: () => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

function authErrorMessage(err: unknown): string {
  if (isAuthError(err)) return SESSION_EXPIRED_MESSAGE;
  if (err instanceof Error) return err.message;
  return 'Der opstod en fejl';
}

export const PortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(portalReducer, initialState);

  useEffect(() => {
    setAuthErrorHandler(() => dispatch({ type: 'SET_ERROR', payload: SESSION_EXPIRED_MESSAGE }));
    return () => setAuthErrorHandler(null);
  }, []);

  /** Lægger validate-access ind i state: en medarbejder, eller admin-tilstand med salonerne. */
  const applyValidation = useCallback(async (data: PortalValidationData) => {
    if (data.employee && data.salon) {
      dispatch({
        type: 'SET_AUTH_SUCCESS',
        payload: {
          employee: { ...data.employee, isOwner: data.isOwner },
          salon: data.salon,
          isOwner: data.isOwner,
          actor: data.actor,
        },
      });
      return;
    }
    if (data.isAdminMode) {
      const salons = await getAdminSalons();
      dispatch({ type: 'SET_ADMIN_MODE', payload: { salons, actor: data.actor } });
      return;
    }
    throw new Error('Adgang nægtet');
  }, []);

  const initializePortal = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const token = readPortalToken(window.location.search);
      if (token) {
        // Strip only after the exchange: a StrictMode re-run must still see the
        // token, and a rejected token must not stay in the address bar either.
        try {
          await exchangePortalToken(token);
        } finally {
          stripPortalTokenFromUrl();
        }
      }

      await applyValidation(await validatePortalAccess());
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: authErrorMessage(err) });
    }
  }, [applyValidation]);

  const selectSalon = useCallback(async (salonId: number) => {
    dispatch({ type: 'SELECT_SALON', payload: salonId });
    try {
      const { employees } = await getAdminEmployees(salonId);
      dispatch({ type: 'SET_AVAILABLE_EMPLOYEES', payload: { employees } });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: authErrorMessage(err) });
    }
  }, []);

  const selectEmployee = useCallback(async (employeeId: number) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await startImpersonation(employeeId);
      await applyValidation(await validatePortalAccess());
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: authErrorMessage(err) });
    }
  }, [applyValidation]);

  const stopImpersonation = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await endImpersonation();
      await applyValidation(await validatePortalAccess());
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: authErrorMessage(err) });
    }
  }, [applyValidation]);

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const value: PortalContextValue = {
    ...state,
    initializePortal,
    selectSalon,
    selectEmployee,
    stopImpersonation,
    reset,
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};

export function usePortal(): PortalContextValue {
  const context = useContext(PortalContext);
  if (!context) throw new Error('usePortal must be used within a PortalProvider');
  return context;
}
